import { usePostHog } from "posthog-js/react";
import { useMemo } from "react";
import { toPostHogTenant } from "~components/shared/postHog/useSetPostHogProperties.ts";
import { useIsDesktop } from "~components/shared/useIsDesktop/useIsDesktop.ts";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import type { PendingPhotoDocUpload } from "./PhotoUpload/photoDocStorage.ts";

type PhotoDocAssignmentType = "work_package" | "manager_account";

type PhotoDocAssignmentTarget = "work_package" | "construction_site_manager";

/** Why the burst camera handed over to the system camera. */
export type BurstCameraUnavailableReason = "camera" | "location";

/** Why an upload attempt failed, mirroring the queue's retry decision. */
export type PhotoDocUploadFailureKind =
  | "client_error"
  | "expired"
  | "missing_data"
  | "transient";

/** What the attempt failed with. Also the Sentry tag `upload.errorKind`. */
export type PhotoDocUploadErrorKind =
  | "blob_create"
  | "blob_store"
  | "missing_data"
  | `api_${number}`
  | "exception"
  | "unknown";

/**
 * Properties shared by every queue event so an upload can be followed from
 * `photo doc upload queued` through its automatic retries to the final outcome.
 */
function uploadProperties(upload: PendingPhotoDocUpload) {
  return {
    uploadId: upload.id,
    tenantId: upload.tenantId,
    photoCount: upload.fileCount,
    retries: upload.retries,
    assignmentType: assignmentTypeOf(upload),
  };
}

function assignmentTypeOf(
  upload: PendingPhotoDocUpload,
): PhotoDocAssignmentType {
  return upload.workPackageId === null ? "manager_account" : "work_package";
}

/**
 * PostHog events for the photo documentation, tracked from the tenant-scoped
 * parts of the app. For the events fired by the background upload queue see
 * `usePhotoDocQueueAnalytics`.
 */
export function usePhotoDocAnalytics() {
  const postHog = usePostHog();
  const isDesktop = useIsDesktop();
  const tenant = toPostHogTenant(useCurrentTenant());

  return useMemo(() => {
    const shared = { tenant, isDesktop };

    return {
      trackUploadQueued: (props: {
        uploadId: string;
        assignmentType: PhotoDocAssignmentType;
        photoCount: number;
        hasComment: boolean;
      }) =>
        postHog.capture("photo doc upload queued", {
          ...shared,
          ...props,
          isOffline: !navigator.onLine,
        }),

      trackUploadQueueFailed: (props: {
        assignmentType: PhotoDocAssignmentType;
        photoCount: number;
      }) =>
        postHog.capture("photo doc upload queue failed", {
          ...shared,
          ...props,
        }),

      trackUploadRetried: (upload: PendingPhotoDocUpload) =>
        postHog.capture("photo doc upload retried", {
          ...shared,
          ...uploadProperties(upload),
          ageMinutes: uploadAgeMinutes(upload),
          wasPermanentFailure: isPermanentFailure(upload),
        }),

      trackUploadDiscarded: (upload: PendingPhotoDocUpload) =>
        postHog.capture("photo doc upload discarded", {
          ...shared,
          ...uploadProperties(upload),
          ageMinutes: uploadAgeMinutes(upload),
          wasPermanentFailure: isPermanentFailure(upload),
        }),

      trackPhotoDeleted: () =>
        postHog.capture("photo doc photo deleted", shared),

      trackPhotoDocDeleted: () => postHog.capture("photo doc deleted", shared),

      trackPhotoDocAssigned: (props: {
        assignedTo: PhotoDocAssignmentTarget;
      }) => postHog.capture("photo doc assigned", { ...shared, ...props }),

      trackBurstCameraOpened: () =>
        postHog.capture("photo doc burst camera opened", shared),

      /** `photoCount` is the series length, so opened/closed gives the drop-off. */
      trackBurstCameraClosed: (props: {
        photoCount: number;
        discarded: boolean;
      }) =>
        postHog.capture("photo doc burst camera closed", {
          ...shared,
          ...props,
        }),

      /**
       * The fallback to the system camera is silent by design, which makes it
       * invisible without this event: it is the only way to see how often the
       * burst camera cannot be offered, and why.
       */
      trackBurstCameraUnavailable: (props: {
        reason: BurstCameraUnavailableReason;
      }) =>
        postHog.capture("photo doc burst camera unavailable", {
          ...shared,
          ...props,
        }),

      trackPhotoDownloaded: () =>
        postHog.capture("photo doc photo downloaded", shared),

      trackPhotoDocDownloaded: (props: {
        photoCount: number;
        totalBytes: number;
        missingCount: number;
        durationMs: number;
      }) =>
        postHog.capture("photo doc group downloaded", { ...shared, ...props }),

      trackPhotoDocDownloadFailed: (props: {
        photoCount: number;
        totalBytes: number;
        reason: string;
      }) =>
        postHog.capture("photo doc group download failed", {
          ...shared,
          ...props,
        }),
    };
  }, [postHog, tenant, isDesktop]);
}

/**
 * PostHog events for the background upload queue, including its automatic
 * retries. The queue runs above `CurrentTenantProvider` and processes uploads
 * of every tenant, so these events carry the upload's `tenantId` instead of the
 * current tenant's short name.
 */
export function usePhotoDocQueueAnalytics() {
  const postHog = usePostHog();
  const isDesktop = useIsDesktop();

  return useMemo(
    () => ({
      trackUploadSucceeded: (
        upload: PendingPhotoDocUpload,
        props: { uploadDurationMs: number; queueWaitMs: number },
      ) =>
        postHog.capture("photo doc upload succeeded", {
          isDesktop,
          ...uploadProperties(upload),
          ...props,
        }),

      trackUploadAttemptFailed: (
        upload: PendingPhotoDocUpload,
        props: {
          failureKind: PhotoDocUploadFailureKind;
          errorKind: PhotoDocUploadErrorKind;
          nextRetryInMs: number | null;
        },
      ) =>
        postHog.capture("photo doc upload attempt failed", {
          isDesktop,
          ...uploadProperties(upload),
          ...props,
          isOnline: navigator.onLine,
        }),
    }),
    [postHog, isDesktop],
  );
}

function uploadAgeMinutes(upload: PendingPhotoDocUpload): number {
  return Math.round((Date.now() - upload.createdAt) / 60_000);
}

function isPermanentFailure(upload: PendingPhotoDocUpload): boolean {
  return upload.nextRetryAt === Number.MAX_SAFE_INTEGER;
}
